import { useMutation } from '@tanstack/react-query';

import { v1Api } from '@/api/backendApi';
import { toast } from '@music-vine/cadence/ui';

interface CustomerPortalResponse {
  Location: string;
}

interface UseStripeCustomerPortalReturn {
  error: Error | null;
  isLoading: boolean;
  openCustomerPortal: () => void;
}

/**
 * Creates a Stripe customer portal session
 */
const createCustomerPortalSession =
  async (): Promise<CustomerPortalResponse> => {
    const response = await v1Api.post<CustomerPortalResponse>(
      'v2/subscription/customerportal'
    );

    const { data } = response;

    if (!data.Location) {
      throw new Error('No portal URL received from server');
    }

    return data;
  };

/**
 * Hook for managing Stripe Customer Portal integration
 *
 * This hook handles creating a Stripe customer portal session and redirecting
 * the user to the Stripe-hosted billing management interface.
 */
export const useStripeCustomerPortal = (): UseStripeCustomerPortalReturn => {
  const mutation = useMutation({
    mutationFn: createCustomerPortalSession,
    onSuccess: (data) => {
      // Redirect to Stripe customer portal
      window.location.href = data.Location;
    },
    onError: (error: Error) => {
      toast.error('Unable to open billing portal.');
      console.error('Error opening Stripe customer portal:', error);
    },
  });

  return {
    openCustomerPortal: mutation.mutate,
    isLoading: mutation.isPending,
    error: mutation.error,
  };
};
